import * as React from "react";
import {useState} from "react";
import {RaisedButton} from "../common/RaisedButton";
import {raisedButtonClassOnlyStyle} from "../common/buttonStyles";
import {useClickOutside} from "../../utils/UseClickOutside";
import {AdminRegion} from "../../api/LoginContentAdminApi";

export interface AdminLoginRegionTabsProps {
    regions: AdminRegion[];
    activeRegionId: number | undefined;
    onRegionSelect: (region: AdminRegion) => void;
    setShowAddRegion: () => void;
}

export const AdminLoginContentRegionTabs: React.FunctionComponent<AdminLoginRegionTabsProps> = (props) => {
    const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
        fr: "French",
        en: "English",
    };
    const EN_CODE = "en";
    const EN_LABEL = "English";
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
    const {targetRef: regionTabsRef} = useClickOutside(() => setOpenDropdownId(null));

    const parentRegions = props.regions.filter(r => !r.parentRegionId);
    const getRegionChildren = (parentId: number) => props.regions.filter(r => r.parentRegionId === parentId);
    const getLanguageLabel = (code: string | null | undefined): string => LANGUAGE_DISPLAY_NAMES[code || "en"] ?? (code || "").toUpperCase();
    const handleSelect = (selectedRegion: AdminRegion) => {
        props.onRegionSelect(selectedRegion);
        setOpenDropdownId(null);
    };

    const renderStatusDot = (published: boolean) => {
        const className = "admin-login-content__region-status"
            + (published ? " admin-login-content__region-status--live" : "");

        return <span className={className} aria-hidden="true"/>;
    };

    const renderTabDropdownOption = (label: string, region: AdminRegion) => {
        const isActive = region.id === props.activeRegionId;
        const className = "admin-login-content__region-dropdown-item"
            + (isActive ? " admin-login-content__region-dropdown-item--active" : "");

        return <RaisedButton
                key={region.id}
                type="button"
                className={className}
                style={raisedButtonClassOnlyStyle}
                primary={false}
                role="option"
                onClick={() => handleSelect(region)}>
                {label}
                {renderStatusDot(region.published)}
            </RaisedButton>;
    };

    const renderTabDropdownOptions = 
        (dropOpen: boolean, region: AdminRegion, childRegions: AdminRegion[]) => {
        if (!dropOpen) return null;

        return <div className="admin-login-content__region-dropdown" role="listbox">
                {renderTabDropdownOption(EN_LABEL, region)}
                {childRegions.map(cr => renderTabDropdownOption(getLanguageLabel(cr.languageCode), cr))}
            </div>;
    };

    const getTabDropdownContentClass = (isGroupActive: boolean) => {
      return `admin-login-content__region-tab${isGroupActive ? " admin-login-content__region-tab--active" : ""}`;
    };

    const renderLanguageBadge = (activeChildRegion: AdminRegion | undefined) => {
        const languageBadge =  activeChildRegion?.languageCode?.toUpperCase() ?? EN_CODE.toUpperCase();
        return <span className="admin-login-content__lang-badge">{languageBadge}</span>;
    };

    const renderDropdownIcon = (dropOpen: boolean) => {
        const className = `fa-solid fa-chevron-down admin-login-content__chevron
        ${dropOpen ? " admin-login-content__chevron--open" : ""}`;
        return <i className={className}/>;
    };

    const renderTabDropdownContent =
        (dropOpen: boolean, parentRegion: AdminRegion, childRegions: AdminRegion[]) => {
            const activeChildRegion = childRegions.find(v => v.id === props.activeRegionId);
            const isGroupActive = parentRegion.id === props.activeRegionId
                || childRegions.some(v => v.id === props.activeRegionId);

            return <RaisedButton
                    type="button"
                    className={getTabDropdownContentClass(isGroupActive)}
                    style={raisedButtonClassOnlyStyle}
                    primary={false}
                    role="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownId(dropOpen ? null : parentRegion.id);
                    }}>
                        {parentRegion.regionName}
                        {renderLanguageBadge(activeChildRegion)}
                        {renderDropdownIcon(dropOpen)}
                        {renderStatusDot(activeChildRegion?.published ?? parentRegion.published)}
                </RaisedButton>;
    };

    const renderDropdownTab = (parentRegion: AdminRegion, childRegions: AdminRegion[]) => {
        const dropOpen = openDropdownId === parentRegion.id;

        return <div key={parentRegion.id} className="admin-login-content__region-tab-group">
                {renderTabDropdownContent(dropOpen, parentRegion, childRegions)}
                {renderTabDropdownOptions(dropOpen, parentRegion, childRegions)}
            </div>;
    };

    const renderSimpleTab = (region: AdminRegion) => {
        const className = "admin-login-content__region-tab"
            + (region.id === props.activeRegionId ? " admin-login-content__region-tab--active" : "");

        return <RaisedButton
                key={region.id}
                type="button"
                className={className}
                style={raisedButtonClassOnlyStyle}
                primary={false}
                role="tab"
                onClick={() => props.onRegionSelect(region)}>
                    {region.regionName}
                    {renderStatusDot(region.published)}
            </RaisedButton>;
    };

    const renderRegionTabs = () => {
        return parentRegions.map(region => {
            const childRegions = getRegionChildren(region.id);
            return childRegions.length > 0
                ? renderDropdownTab(region, childRegions)
                : renderSimpleTab(region);
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Escape" && openDropdownId !== null)
            setOpenDropdownId(null);
    };

    const renderAddRegionButton = () => {
      return  <RaisedButton
          type="button"
          className="admin-login-content__add-region-tab"
          style={raisedButtonClassOnlyStyle}
          onClick={() => props.setShowAddRegion()}
          primary={false}>
          <i className="fa-solid fa-plus"/>Add Region
      </RaisedButton>;
    };

    return <div ref={regionTabsRef}
            className="admin-login-content__region-tabs"
            role="tablist"
            onKeyDown={handleKeyDown}
            onClick={() => setOpenDropdownId(null)}>
            {renderRegionTabs()}
            {renderAddRegionButton()}
        </div>;
};
